/**
 * ITER302 · Voucher Studio — design tokens.
 *
 * Central registry of the visual vocabulary used by the shared
 * <VoucherCard> component.  Editing this file cascades to the Studio
 * live preview, the voucher-types table thumbnail, and the guest
 * wallet render.  Keep all keys as plain strings so they round-trip
 * safely through JSON.
 */

// Palette map — each palette drives:
//   • the card background gradient / solid
//   • the primary text colour
//   • the accent (used for face value, borders, QR tint)
export const PALETTES = {
  dark_green_gold: {
    label: 'Dark Green + Gold',
    background: 'linear-gradient(135deg, #143E30 0%, #0E2C22 100%)',
    surface: '#143E30',
    text: '#F7F5EE',
    textMuted: 'rgba(247,245,238,0.72)',
    accent: '#C9A227',
    qrBg: '#F7F5EE',
    qrFg: '#143E30',
  },
  navy_gold: {
    label: 'Navy + Gold',
    background: 'linear-gradient(135deg, #0E2543 0%, #071634 100%)',
    surface: '#0E2543',
    text: '#F5F1E4',
    textMuted: 'rgba(245,241,228,0.72)',
    accent: '#D4AF37',
    qrBg: '#F5F1E4',
    qrFg: '#0E2543',
  },
  wine_gold: {
    label: 'Wine + Gold',
    background: 'linear-gradient(135deg, #4A1220 0%, #2D0A14 100%)',
    surface: '#4A1220',
    text: '#F7EDDD',
    textMuted: 'rgba(247,237,221,0.72)',
    accent: '#E4B942',
    qrBg: '#F7EDDD',
    qrFg: '#4A1220',
  },
  charcoal_gold: {
    label: 'Charcoal + Gold',
    background: 'linear-gradient(135deg, #1F1F23 0%, #101013 100%)',
    surface: '#1F1F23',
    text: '#EFEEE9',
    textMuted: 'rgba(239,238,233,0.68)',
    accent: '#D4AF37',
    qrBg: '#EFEEE9',
    qrFg: '#1F1F23',
  },
  cream_dark_green: {
    label: 'Cream + Dark Green',
    background: 'linear-gradient(135deg, #F5EFDD 0%, #EFE7CE 100%)',
    surface: '#F5EFDD',
    text: '#143E30',
    textMuted: 'rgba(20,62,48,0.66)',
    accent: '#B8860B',
    qrBg: '#F5EFDD',
    qrFg: '#143E30',
  },
  white_dark_green: {
    label: 'White + Dark Green',
    background: 'linear-gradient(135deg, #FFFFFF 0%, #F1EEE6 100%)',
    surface: '#FFFFFF',
    text: '#143E30',
    textMuted: 'rgba(20,62,48,0.62)',
    accent: '#143E30',
    qrBg: '#FFFFFF',
    qrFg: '#143E30',
  },
};

export const DEFAULT_PALETTE_KEY = 'dark_green_gold';

export function paletteFor(design) {
  const key = design?.theme && PALETTES[design.theme]
    ? design.theme
    : DEFAULT_PALETTE_KEY;
  const p = { ...PALETTES[key] };
  if (design?.accent_color) p.accent = design.accent_color;
  return p;
}

// Icons visible in the studio icon picker and rendered on the card.
// Uses lucide-react component names so consumers can import the
// component lazily.
export const CARD_ICONS = [
  { key: 'dining',      label: 'Dining',      lucide: 'Utensils' },
  { key: 'drinks',      label: 'Drinks',      lucide: 'Wine' },
  { key: 'celebration', label: 'Celebration', lucide: 'Cake' },
  { key: 'gift',        label: 'Gift',        lucide: 'Gift' },
  { key: 'crown',       label: 'Premium',     lucide: 'Crown' },
];

export const BADGE_PRESETS = [
  { key: 'None',            label: 'No badge' },
  { key: 'Exclusive',       label: 'Exclusive' },
  { key: 'Premium',         label: 'Premium' },
  { key: 'Limited Edition', label: 'Limited Edition' },
  { key: 'VIP',             label: 'VIP' },
];

export const RIBBON_VARIANTS = [
  { key: 'none',          label: 'None' },
  { key: 'corner_bow',    label: 'Corner Bow' },
  { key: 'diagonal',      label: 'Diagonal Ribbon' },
  { key: 'banner',        label: 'Top Banner' },
  { key: 'side_accent',   label: 'Side Accent' },
  // ITER302-refine · 4 additional variants.
  { key: 'top_banner',    label: 'Top Banner (wide)' },
  { key: 'bottom_sash',   label: 'Bottom Sash' },
  { key: 'double_corner', label: 'Double Corner' },
  { key: 'wrap_ribbon',   label: 'Wrap Ribbon' },
];

// ── ITER302-refine · Background pattern overlays ──
export const BG_PATTERNS = [
  { key: 'none',            label: 'None' },
  { key: 'frame',           label: 'Certificate Frame' },
  { key: 'diagonal_lines',  label: 'Cross-Hatch' },
  { key: 'corner_flourish', label: 'Corner Flourish' },
  { key: 'shimmer',         label: 'Shimmer' },
];

// ── Loyalty-P2 · Premium visual layer ──
// Optional decorative treatments layered ON TOP of the palette.
// All keys are additive & backward compatible — a voucher without any
// of these fields renders exactly as before.
export const CORNER_STYLES = [
  { key: 'none',    label: 'None' },
  { key: 'notched', label: 'Notched (ticket)' },
  { key: 'rounded', label: 'Rounded (soft)' },
  { key: 'bracket', label: 'Bracket (elegant)' },
];

export const ACCENT_STROKE_STYLES = [
  { key: 'none',    label: 'None' },
  { key: 'thin',    label: 'Thin gold line' },
  { key: 'double',  label: 'Double gold line' },
  { key: 'dashed',  label: 'Dashed premium' },
];

export const CHIP_LABEL_PRESETS = [
  { key: 'none',            label: 'No chip' },
  { key: 'Premium',         label: 'Premium' },
  { key: 'VIP',             label: 'VIP' },
  { key: 'Special',         label: 'Special' },
  { key: 'Exclusive',       label: 'Exclusive' },
  { key: 'Signature',       label: 'Signature' },
  { key: 'Limited Edition', label: 'Limited Edition' },
];

// ── ITER302-refine-v2 · Content vertical distribution ──
export const CONTENT_ALIGNMENTS = [
  { key: 'centered', label: 'Centered (recommended)' },
  { key: 'top',      label: 'Top-aligned (legacy)' },
];

/**
 * Return three shades of a hex accent colour for ribbon depth:
 *   highlight — lighter, used for the highlight edge
 *   base      — the input colour
 *   shadow    — darker, used for the fold/underside
 * Simple RGB scale — good enough for premium-looking, non-flat SVG art.
 */
export function accentShades(hex) {
  const h = (hex || '#C9A227').replace('#', '');
  const n = h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h.padEnd(6, '0').slice(0, 6);
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  const mix = (v, t, f) => Math.max(0, Math.min(255, Math.round(v + (t - v) * f)));
  const toHex = (v) => v.toString(16).padStart(2, '0');
  const light  = `#${toHex(mix(r, 255, 0.35))}${toHex(mix(g, 255, 0.35))}${toHex(mix(b, 255, 0.35))}`;
  const shadow = `#${toHex(mix(r, 0,   0.42))}${toHex(mix(g, 0,   0.42))}${toHex(mix(b, 0,   0.42))}`;
  return { light, base: `#${n}`, shadow };
}

// ── ITER302-refine · Image placement ──
export const IMAGE_POSITIONS = [
  { key: 'bottom_right',  label: 'Bottom Right' },
  { key: 'bottom_left',   label: 'Bottom Left' },
  { key: 'bottom_center', label: 'Bottom Center' },
  { key: 'middle_center', label: 'Middle Center' },
  { key: 'top_right',     label: 'Top Right' },
  { key: 'top_left',      label: 'Top Left' },
  { key: 'background',    label: 'Background (full bleed)' },
];

export const IMAGE_SIZES = [
  { key: 'small',      label: 'Small (60px)',    px: 60 },
  { key: 'medium',     label: 'Medium (90px)',   px: 90 },
  { key: 'large',      label: 'Large (120px)',   px: 120 },
  { key: 'full_width', label: 'Full width',      px: null },
];

// ── ITER302-refine · Typography ──
// The list is intentionally an array of plain family strings so more
// Google Fonts can be added without any code change.
export const HEADING_FONTS = [
  { key: 'Montserrat',         label: 'Montserrat (default)' },
  { key: 'Playfair Display',   label: 'Playfair Display (serif)' },
  { key: 'Poppins',            label: 'Poppins (modern)' },
  { key: 'Cormorant Garamond', label: 'Cormorant Garamond (luxury)' },
  { key: 'Oswald',             label: 'Oswald (bold)' },
  { key: 'Raleway',            label: 'Raleway (clean)' },
];

export const BODY_FONTS = [
  { key: 'Inter',      label: 'Inter (default)' },
  { key: 'Montserrat', label: 'Montserrat' },
  { key: 'Poppins',    label: 'Poppins' },
  { key: 'Raleway',    label: 'Raleway' },
  { key: 'Nunito',     label: 'Nunito' },
];

export const VALUE_SIZES = [
  { key: 'small',  label: 'Small',       px: 30 },
  { key: 'medium', label: 'Medium',      px: 38 },
  { key: 'large',  label: 'Large',       px: 46 },
  { key: 'xlarge', label: 'Extra Large', px: 56 },
];

export const LETTER_SPACINGS = [
  { key: 'normal',      label: 'Normal',      value: '0.02em' },
  { key: 'wide',        label: 'Wide',        value: '0.18em' },
  { key: 'extra_wide',  label: 'Extra Wide',  value: '0.32em' },
];

/**
 * Idempotently ensure the given Google Font families are loaded via a
 * single <link> tag in <head>. Only ONE voucher-font link is kept at a
 * time — updating replaces the existing tag rather than accumulating
 * stale ones as fonts are switched in the Studio.
 */
export function ensureGoogleFonts(families) {
  if (typeof document === 'undefined') return;
  const cleaned = Array.from(new Set((families || []).filter(Boolean)));
  if (!cleaned.length) return;
  const family = cleaned
    .map((f) => `family=${encodeURIComponent(f)}:wght@400;600;700`)
    .join('&');
  const href = `https://fonts.googleapis.com/css2?${family}&display=swap`;
  // Reuse the single dedicated <link> if present; noop when the href
  // already matches, otherwise swap in the new one.
  const KEY = 'voucher-fonts-active';
  const existing = document.querySelector(`link[data-voucher-fonts="${KEY}"]`);
  if (existing) {
    if (existing.getAttribute('href') === href) return;
    existing.setAttribute('href', href);
    return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.setAttribute('data-voucher-fonts', KEY);
  document.head.appendChild(link);
}
